import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowsClockwise, IdentificationCard, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * PG67 Audit — firm-wide Didit identity verification compliance ledger.
 *
 * Every client verification via Didit gets a row here. Rows highlight:
 *  - Green: fully approved by our compliance gate (HMLR-ready)
 *  - Amber: Didit approved but our gate flagged issues (e.g. driving licence)
 *  - Red: declined
 *  - Slate: in-progress / not started
 */
function currentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  return `${now.getUTCFullYear()}-Q${q}`;
}

function quarterOptions() {
  const now = new Date();
  const opts = [];
  let y = now.getUTCFullYear();
  let q = Math.floor(now.getUTCMonth() / 3) + 1;
  for (let i = 0; i < 8; i++) {
    opts.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return opts;
}

export default function Pg67AuditPage() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [nameReviews, setNameReviews] = useState([]);
  const [clearingSid, setClearingSid] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/didit/audit", { params: { quarter } });
      setData(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }, [quarter]);

  const fetchNameReviews = useCallback(async () => {
    try {
      const { data } = await API.get("/didit/name-reviews");
      setNameReviews(data?.name_reviews || []);
    } catch { /* non-fatal — section just stays hidden */ }
  }, []);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);
  useEffect(() => { fetchNameReviews(); }, [fetchNameReviews]);

  const clearOne = async (sid) => {
    setClearingSid(sid);
    try {
      await API.post(`/didit/sessions/${sid}/approve-name-review`);
      toast.success("Name confirmed — ID check cleared");
      setNameReviews((rows) => rows.filter((r) => r.session_id !== sid));
      fetchAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear this review");
    } finally {
      setClearingSid(null);
    }
  };

  const clearAll = async () => {
    setClearingAll(true);
    const sids = nameReviews.map((r) => r.session_id);
    let ok = 0;
    for (const sid of sids) {
      try {
        await API.post(`/didit/sessions/${sid}/approve-name-review`);
        ok += 1;
      } catch { /* skip failures, keep going */ }
    }
    toast.success(`Cleared ${ok} of ${sids.length} name review${sids.length === 1 ? "" : "s"}`);
    await fetchNameReviews();
    await fetchAudit();
    setClearingAll(false);
  };

  const summary = data?.summary || {};
  const rate = summary.approval_rate ?? 0;
  const perfect = rate >= 100;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="pg67-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <IdentificationCard weight="fill" className="w-6 h-6 text-indigo-600" />
            Practice Guide 67 Audit
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Firm-wide identity verification ledger — every Didit KYC check with NFC + liveness + AML + address evidence, per HMLR PG67.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="w-36" data-testid="pg67-quarter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {quarterOptions().map((q) => (
                <SelectItem key={q} value={q}>{q}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAudit} data-testid="pg67-refresh-btn">
            <ArrowsClockwise className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className={`p-3 ${perfect ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="pg67-tile-rate">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Approval rate</div>
          <div className={`text-2xl font-bold mt-1 ${perfect ? "text-emerald-700" : "text-amber-700"}`}>{rate}%</div>
          <div className="text-[10px] text-slate-500 mt-0.5">of completed checks</div>
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Total checks</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{summary.total || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Approved</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{summary.approved || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">In Review</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{summary.in_review || 0}</div>
        </Card>
        <Card className={`p-3 ${(summary.declined || 0) > 0 ? "bg-rose-50 border-rose-200" : ""}`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Declined</div>
          <div className={`text-2xl font-bold mt-1 ${(summary.declined || 0) > 0 ? "text-rose-700" : "text-slate-400"}`}>{summary.declined || 0}</div>
        </Card>
      </div>

      {err && <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-md mb-3">{err}</div>}

      {nameReviews.length > 0 && (
        <Card className="mb-4 border-indigo-200 bg-indigo-50/40 overflow-hidden" data-testid="pg67-name-reviews">
          <div className="px-4 py-3 border-b border-indigo-100 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                <Warning weight="fill" className="w-4 h-4 text-indigo-600" />
                Name reviews to clear ({nameReviews.length})
              </h2>
              <p className="text-[11px] text-indigo-700/80 mt-0.5">
                Didit flagged a "middle name mismatch" because a UK passport's combined given names were split by the client. The full name matches — confirm to clear each check.
              </p>
            </div>
            <Button size="sm" onClick={clearAll} disabled={clearingAll}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="pg67-name-review-clear-all">
              <CheckCircle weight="fill" className="w-4 h-4 mr-1" />
              {clearingAll ? "Clearing…" : "Clear all"}
            </Button>
          </div>
          <div className="divide-y divide-indigo-100">
            {nameReviews.map((r) => (
              <div key={r.session_id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`pg67-name-review-row-${r.session_id}`}>
                <div className="min-w-0 text-xs">
                  <div className="font-medium text-slate-900 truncate">
                    {r.client_name || r.client_email || "—"}
                    {r.case_reference && <span className="text-slate-400 font-normal"> · {r.case_reference}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Passport: <strong className="text-slate-700">{r.extracted_name || "—"}</strong>
                    <span className="mx-1.5 text-slate-300">→</span>
                    split into <strong className="text-slate-700">{r.confirmed_first || "—"}</strong>
                    {r.confirmed_middle ? <> / <strong className="text-slate-700">{r.confirmed_middle}</strong></> : null}
                    {r.confirmed_surname ? <> {r.confirmed_surname}</> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.case_id && (
                    <Link to={`/cases/${r.case_id}`} className="text-[11px] text-indigo-600 hover:underline">Case →</Link>
                  )}
                  <Button size="sm" variant="outline" onClick={() => clearOne(r.session_id)} disabled={clearingSid === r.session_id || clearingAll}
                    className="h-7 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid={`pg67-name-review-clear-${r.session_id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                    {clearingSid === r.session_id ? "Clearing…" : "Confirm & clear"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Document</th>
                <th className="text-left px-3 py-2">NFC</th>
                <th className="text-left px-3 py-2">Liveness</th>
                <th className="text-left px-3 py-2">AML</th>
                <th className="text-left px-3 py-2">Address</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Gate</th>
                <th className="text-right px-3 py-2">Case</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="text-center py-8 text-slate-400">Loading…</td></tr>}
              {!loading && (data?.verifications || []).length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-slate-400">No verifications in this window.</td></tr>
              )}
              {!loading && (data?.verifications || []).map((v) => {
                const ev = v.evidence || {};
                const blocking = (v.compliance_failures || []).filter((r) => !r.startsWith("[sandbox]"));
                const gateOk = blocking.length === 0 && (v.status || "").toLowerCase() === "approved";
                return (
                  <tr key={v.session_id} data-testid={`pg67-row-${v.session_id}`}
                      className={`border-t border-slate-100 ${blocking.length > 0 ? "bg-rose-50" : ""}`}>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {v.created_at ? new Date(v.created_at).toLocaleDateString("en-GB") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900 truncate max-w-[180px]" title={v.client_email}>{v.client_email || "—"}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {ev.document_type || "—"}
                      {ev.document_number && <div className="text-[10px] text-slate-500">{ev.document_number}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {ev.nfc_verified ? <span className="text-emerald-700">✓</span>
                        : v.environment === "sandbox" ? <span className="text-sky-600 text-[10px]">sandbox</span>
                        : <span className="text-rose-600">✗</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={(ev.liveness?.method || "").startsWith("ACTIVE") ? "text-emerald-700" : "text-rose-600"}>
                        {ev.liveness?.method || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={ev.aml?.status === "Approved" ? "text-emerald-700" : "text-slate-500"}>
                        {ev.aml?.status || "—"}
                      </span>
                      {ev.aml?.hits?.length > 0 && <span className="text-rose-600 text-[10px] ml-1">{ev.aml.hits.length} hit</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={ev.address?.status === "Approved" ? "text-emerald-700" : "text-slate-500"}>
                        {ev.address?.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] uppercase">{v.status || "—"}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {gateOk ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1 text-[10px] font-medium">
                          <CheckCircle weight="fill" className="w-3 h-3" /> HMLR ✓
                        </span>
                      ) : blocking.length > 0 ? (
                        <span className="text-rose-700 inline-flex items-center gap-1 text-[10px] font-medium" title={blocking.join("\n")}>
                          <Warning weight="fill" className="w-3 h-3" /> {blocking.length} issue{blocking.length > 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-[10px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {v.case_id && (
                        <Link to={`/cases/${v.case_id}`} className="text-[11px] text-indigo-600 hover:underline">Case →</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
